/*
 * Zero-dependency SRP email probe — decides which branch the email-first login
 * takes WITHOUT sending a password. It fires the FIRST leg of USER_SRP_AUTH
 * (InitiateAuth) and reads the outcome, then NEVER answers the PASSWORD_VERIFIER
 * challenge: no password is sent, no token is minted, and the account's
 * failed-sign-in counter is NOT incremented (that only advances on a wrong
 * RespondToAuthChallenge, which we never send).
 *
 * Raw fetch only (no amazon-cognito-identity-js) so the probe is tiny and
 * independently testable; fetchImpl is injectable, matching the oidc.ts idiom.
 */
import { myApiBase } from '../data/myApi'
import { readAuthConfig } from './authConfig'
import { parseRetryAfter } from './retryAfter'
import { normalizeEmail } from './normalizeEmail'

export type ProbeResult =
  | 'exists' // ambiguous: SRP probe cannot distinguish new vs existing (PreventUserExistenceErrors)
  | 'exists-confirmed' // AUTHORITATIVE (my-api AdminGetUser): a confirmed existing account
  | 'no-account'
  | 'unconfirmed'
  | 'reset-required'
  | 'indeterminate'

/** Authoritative probe outcome (Duncan checklist #3, BOARDROOM-2026-09-02-
 *  191616). `username` is the case-CORRECT Cognito username the account
 *  resolved to (the pool is case-sensitive; legacy usernames were minted
 *  as-typed). Subsequent auth calls — forgot-password above all, where
 *  PreventUserExistenceErrors makes a mis-cased username a SILENT no-op — must
 *  target this value, not a re-derived one. When the server cannot resolve
 *  (SRP fallback, not-found, degraded), it is the canonical lowercase form. */
export interface ProbeOutcome {
  result: ProbeResult
  username: string
  /** true ONLY when my-api's AdminGetUser probe answered. The SRP fallback is
   *  NOT authoritative (PreventUserExistenceErrors makes it say 'exists' for
   *  any address), so the claim-by-email-code path fails CLOSED without it. */
  authoritative?: boolean
  /** my-api answered 429 (per-IP limiter, #275): the caller shows the
   *  "Too many attempts…" copy instead of guessing. */
  rateLimited?: boolean
  /** With rateLimited: my-api's Retry-After, in seconds (the login countdown). */
  retryAfterSeconds?: number
}

/** A large random hex SRP_A. Cognito accepts any well-formed SRP_A for the
 *  InitiateAuth leg; because we never complete the challenge, its
 *  cryptographic correctness is irrelevant. */
function randomSrpA(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(256))
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** Extract the AWS `__type` short name from a cognito-idp JSON error body
 *  (handles both "Foo" and "com.amazonaws...#Foo" forms). */
function errorType(body: string): string {
  try {
    const t = (JSON.parse(body) as { __type?: string }).__type ?? ''
    return t.split('#').pop() ?? ''
  } catch {
    return ''
  }
}

/**
 * AUTHORITATIVE branch discriminator (CMT-2026-08-28-006). Asks my-api's
 * POST /account/probe — which answers via AdminGetUser, immune to
 * PreventUserExistenceErrors — and maps its status onto the routing
 * vocabulary. Any failure (endpoint absent/503/network/no api base) falls
 * back to the SRP probe below, so the previous behaviour is the floor and a
 * my-api outage can never make login worse than before.
 */
export async function probeEmailAuthoritative(
  email: string,
  fetchImpl: typeof fetch = fetch,
  apiBase: string | null = myApiBase(),
): Promise<ProbeOutcome> {
  // The canonical lowercase identity — the username every fallback path (and
  // every account CREATED from here on) uses.
  const canonical = normalizeEmail(email)
  if (apiBase) {
    try {
      const res = await fetchImpl(`${apiBase.replace(/\/$/, '')}/account/probe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // DELIBERATELY case-preserved (NFC + trim only): the case-sensitive
        // pool's legacy usernames were minted as-typed, and the server probes
        // canonical-lowercase FIRST with an as-typed fallback (checklist #3).
        // Pre-lowercasing here would destroy the fallback and leave mixed-case
        // legacy accounts probing not-found → re-register → duplicate loop.
        // This is a probe, not a credentialed call — every credential-bearing
        // wire value remains canonical-first (cognitoAuth.ts).
        body: JSON.stringify({ email: email.normalize('NFC').trim() }),
      })
      // Rate-limited (#275 per-IP limiter): do NOT fall back to the SRP probe
      // (that would just add load) — report it so the caller can say so.
      if (res.status === 429) {
        // Header first (exposed by my-api), else the body's retry_after.
        const body = (await res.json?.().catch(() => ({}))) as { retry_after?: unknown } | undefined
        const retryAfterSeconds = parseRetryAfter(res.headers?.get?.('Retry-After'))
          ?? parseRetryAfter(typeof body?.retry_after === 'number' ? String(body.retry_after) : undefined)
        return { result: 'indeterminate', username: canonical, rateLimited: true, ...(retryAfterSeconds ? { retryAfterSeconds } : {}) }
      }
      if (res.ok) {
        const json = (await res.json().catch(() => ({}))) as { status?: string; username?: string }
        // The server-RESOLVED case-correct username (canonical when it cannot
        // resolve); guard the type so a degraded payload degrades to canonical.
        const username = typeof json.username === 'string' && json.username ? json.username : canonical
        switch (json.status) {
          case 'confirmed':
            return { result: 'exists-confirmed', username, authoritative: true }
          case 'not-found':
            return { result: 'no-account', username, authoritative: true }
          case 'unconfirmed':
            return { result: 'unconfirmed', username, authoritative: true }
          case 'reset-required':
            return { result: 'reset-required', username, authoritative: true }
          case 'force-change':
            // A half-provisioned remnant: the neutral create branch repairs it
            // via the signUp/UsernameExists crossover — same as ambiguous.
            return { result: 'exists', username, authoritative: true }
          // Unknown status vocabulary → fall through to the SRP probe.
        }
      }
      // Non-ok (503 probe_unavailable, 400, …) → SRP fallback below.
    } catch {
      // Network failure on the authoritative call → SRP fallback below.
    }
  }
  // SRP fallback: no case-fallback candidates here — PreventUserExistenceErrors
  // on the prod pool makes the SRP probe answer 'exists' for ANY username, so
  // a second-candidate probe could never distinguish anything (dead code by
  // construction). Canonical username is the honest floor.
  return { result: await probeEmail(email, fetchImpl), username: canonical }
}

export async function probeEmail(email: string, fetchImpl: typeof fetch = fetch): Promise<ProbeResult> {
  const config = readAuthConfig()
  if (!config) return 'indeterminate'

  let res: Response
  try {
    res = await fetchImpl(`https://cognito-idp.${config.region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'USER_SRP_AUTH',
        ClientId: config.userPoolClientId,
        AuthParameters: { USERNAME: normalizeEmail(email), SRP_A: randomSrpA() },
      }),
    })
  } catch {
    // Network/CORS failure — cannot decide; caller degrades to the PASSWORD step.
    return 'indeterminate'
  }

  if (res.ok) {
    const json = (await res.json().catch(() => ({}))) as { ChallengeName?: string }
    // A challenge back means the account exists and SRP reached the verifier
    // step (we STOP here — no password, no token).
    return json.ChallengeName === 'PASSWORD_VERIFIER' ? 'exists' : 'indeterminate'
  }

  const detail = await res.text().catch(() => '')
  switch (errorType(detail)) {
    case 'UserNotFoundException':
      return 'no-account'
    case 'UserNotConfirmedException':
      return 'unconfirmed'
    case 'PasswordResetRequiredException':
      return 'reset-required'
    case 'NotAuthorizedException':
      // USER_SRP_AUTH disabled on the app-client — a build/infra misconfig, not
      // a user state. Surface as an error (spec); the UI degrades gracefully.
      throw new Error('SRP auth flow is not enabled on the consumer app-client.')
    default:
      return 'indeterminate'
  }
}
